"use client";

import { useId, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Radio } from "./Radio";

/* Phase 4.9 — RadioGroup.
 *
 * `Radio` on its own has no way to say that several radios are ONE choice.
 * Grouping was left to the shared `name` attribute, which the browser uses
 * for keyboard and selection behaviour but which carries no semantics for
 * assistive tech: a screen reader met three unrelated radios and never
 * announced the question they answered. There was also nowhere to put a
 * validation message for the group as a whole — only per-option errors,
 * which is the wrong shape for "you must pick one".
 *
 * <fieldset> + <legend> is the fix, and it is deliberately the native
 * elements rather than role="radiogroup" on a div: the native pair gets
 * label association, grouping and the legend's announcement for free in
 * every browser and AT combination, where the ARIA version has to
 * reimplement all three and commonly gets the legend wrong.
 *
 * `aria-invalid` and `aria-describedby` are on the fieldset so an error
 * belongs to the question, not to whichever option happened to be last.
 */

export interface RadioGroupOption<T extends string = string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string = string> {
  /** The question. Rendered as the <legend>. */
  legend: ReactNode;
  options: ReadonlyArray<RadioGroupOption<T>>;
  value: T | null;
  onChange: (next: T) => void;
  name?: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Lay the options out in a row. Only sensible for two or three short ones. */
  orientation?: "vertical" | "horizontal";
  /** Hides the legend visually. It is still announced. */
  hideLegend?: boolean;
  className?: string;
}

export function RadioGroup<T extends string = string>({
  legend,
  options,
  value,
  onChange,
  name: providedName,
  help,
  error,
  required,
  disabled,
  orientation = "vertical",
  hideLegend,
  className,
}: RadioGroupProps<T>) {
  const autoId = useId();
  const name = providedName ?? autoId;
  const helpId = help ? `${autoId}-help` : undefined;
  const errorId = error ? `${autoId}-err` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset
      /* No `--error` modifier: the error state is carried by the message
         below and by aria-invalid on each radio. d2d-DetectHub eval F16: it
         sat on the fieldset, where screen readers do not report it. */
      className={cn("rc-radiogroup", `rc-radiogroup--${orientation}`, className)}
      disabled={disabled}
      aria-describedby={describedBy}
      aria-required={required || undefined}
    >
      <legend className={cn("rc-radiogroup__legend", hideLegend && "sr-only")}>
        {legend}
        {required ? <span className="rc-radiogroup__required" aria-hidden="true">*</span> : null}
      </legend>

      <div className="rc-radiogroup__options">
        {options.map((o) => (
          <Radio
            key={o.value}
            name={name}
            value={o.value}
            label={o.label}
            description={o.description}
            disabled={o.disabled || disabled}
            checked={value === o.value}
            aria-invalid={error ? true : undefined}
            onChange={(checked) => { if (checked) onChange(o.value); }}
          />
        ))}
      </div>

      {help ? <span id={helpId} className="rc-radiogroup__help">{help}</span> : null}
      {/* The error lives on the GROUP, and is announced once — not repeated
          under every option, which is what per-Radio errors would do. */}
      {error ? <span id={errorId} className="rc-radiogroup__error" role="alert">{error}</span> : null}
    </fieldset>
  );
}
